import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles.css';
// 导入API服务
import { sendMessage, textToSpeech, speechToText } from '../../services/api';

// 为Web Speech API添加类型声明
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
    SpeechSynthesis: any;
    SpeechSynthesisUtterance: any;
  }
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

const Chat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showFullscreenInteraction, setShowFullscreenInteraction] = useState(true);
  
  // 语音识别和合成的引用
  const recognitionRef = useRef<any>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  
  // 模拟AI角色数据
  const aiPersona = {
    name: id === 'adil' ? 'Adil' : 
          id === 'eva' ? 'Eva' : 
          id === 'dean' ? 'Dean' : 
          id === 'max' ? 'Max' : 'Assistant',
    avatar: id === 'adil' ? './img/Rectangle 1092 (1).png' :
           id === 'eva' ? './img/eva.png' :
           id === 'dean' ? './img/Rectangle 1093.png' :
           id === 'max' ? './img/Rectangle 1094.png' :
           './img/eva.png',
  };

  // 滚动到最新消息
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // 初始化连接状态
  useEffect(() => {
    setIsConnecting(true);
    // 模拟连接过程
    setTimeout(() => {
      setIsConnecting(false);
    }, 2000);
    
    // 初始化音频元素
    const audioElement = new Audio();
    audioElementRef.current = audioElement;
    
    // 组件卸载时清理资源
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause();
      }
    };
  }, []);

  // 实现语音识别
  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error('麦克风权限请求失败:', error);
      return false;
    }
  };

  const startListening = async () => {
    try {
      // 首先请求麦克风权限
      const hasPermission = await requestMicrophonePermission();
      if (!hasPermission) {
        alert('Please allow microphone access to use voice features');
        return;
      }

      setIsListening(true);
      // 使用Web Speech API进行语音识别
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        throw new Error('Your browser does not support speech recognition');
      }
      
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      
      // 配置语音识别 - 自动检测语言
      // 默认使用中文识别，但会根据用户输入自动调整
      recognition.lang = 'zh-CN'; 
      recognition.continuous = false; // 不持续识别
      recognition.interimResults = true; // 返回临时结果
      
      // 处理识别结果
      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        
        setRecognizedText(transcript);
        
        // 如果是最终结果，则停止识别并处理文本
        if (event.results[0].isFinal) {
          stopListening(transcript);
        }
      };
      
      // 处理错误
      recognition.onerror = (event: any) => {
        console.error('语音识别错误:', event.error);
        setIsListening(false);
      };
      
      // 处理识别结束
      recognition.onend = () => {
        // 如果还在监听状态但识别已结束，可能是自动结束，此时需要处理已识别的文本
        if (isListening && recognizedText) {
          stopListening(recognizedText);
        } else if (isListening) {
          // 如果没有识别到文本但仍在监听状态，则结束监听
          setIsListening(false);
        }
        // 确保在每次识别结束后释放资源
        if (recognitionRef.current) {
          recognitionRef.current = null;
        }
      };
      
      // 开始识别
      recognition.start();
    } catch (error) {
      console.error('语音识别失败:', error);
      setIsListening(false);
    }
  };

  const stopListening = async (text: string) => {
    // 停止语音识别
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    setIsListening(false);
    if (text.trim()) {
      // 添加用户消息
      const userMessage: Message = {
        id: Date.now().toString(),
        text: text,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
      setRecognizedText('');
      
      // 更新聊天历史
      const updatedHistory = [...chatHistory, { role: 'user', content: text }];
      setChatHistory(updatedHistory);
      
      // 显示AI思考状态
      setIsAiSpeaking(true);
      
      try {
        // 调用DeepSeek API获取回复
        const aiReply = await sendMessage(text, updatedHistory);
        
        // 添加AI消息
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: aiReply,
          sender: 'ai',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, aiMessage]);
        
        // 更新聊天历史
        setChatHistory([...updatedHistory, { role: 'assistant', content: aiReply }]);
        
        // 将AI回复转换为语音（斯嘉丽·约翰逊音色）
        const speechResult = await textToSpeech(aiReply);
        if (speechResult.success) {
          // 播放语音
          playAudio(speechResult.audioUrl);
        }
      } catch (error) {
        console.error('AI回复失败:', error);
      } finally {
        setIsAiSpeaking(false);
      }
    }
  };
  
  // 播放音频
  const playAudio = (audioUrl: string) => {
    if (audioElementRef.current) {
      // 设置音频源
      audioElementRef.current.src = audioUrl;
      
      // 添加加载错误处理
      audioElementRef.current.onerror = (e) => {
        console.error('音频播放失败:', e);
        setIsAiSpeaking(false);
      };
      
      // 添加加载成功处理
      audioElementRef.current.onloadeddata = () => {
        audioElementRef.current?.play().catch(error => {
          console.error('音频播放失败:', error);
          setIsAiSpeaking(false);
        });
      };
    }
  };

  const handleBackClick = () => {
    navigate('/home');
  };

  const handleCancelRecording = () => {
    setIsListening(false);
    setRecognizedText('');
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="back-button" onClick={handleBackClick}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="chat-header-info">
          <img src={aiPersona.avatar} alt={aiPersona.name} className="chat-avatar" />
          <div className="chat-name">{aiPersona.name}</div>
        </div>
        <div className="header-spacer"></div>
      </div>

      {showFullscreenInteraction ? (
        <div className="fullscreen-interaction">
          {isConnecting ? (
            <div className="interaction-content">
              <div className="interaction-visual">
                <img src="/img/material/Statu 状态=Connecting.png" alt="Connecting" className="pulse-effect" />
              </div>
              <div className="interaction-text">Connecting...</div>
              <button className="cancel-button" onClick={() => setIsConnecting(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : isListening ? (
            <div className="interaction-content">
              <div className="interaction-visual">
                <img src="/img/material/Statu 状态=Listening.png" alt="Listening" className="breathing-effect" />
              </div>
              <div className="interaction-text">Tap to interrupt</div>
              <button className="cancel-button" onClick={handleCancelRecording}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : isAiSpeaking ? (
            <div className="interaction-content">
              <div className="interaction-visual">
                <img src="/img/material/Statu 状态=Speaking.png" alt="Speaking" className="wave-effect" />
              </div>
              <div className="interaction-text">Tap to cancel</div>
              <button className="cancel-button" onClick={() => {
                if (audioElementRef.current) {
                  audioElementRef.current.pause();
                  audioElementRef.current.currentTime = 0;
                }
                setIsAiSpeaking(false);
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <div className="interaction-content">
              <div className="interaction-visual">
                <img src="/img/material/Statu 状态=Analyzing.png" alt="Ready" className="pulse-effect" />
              </div>
              <div className="start-prompt">Start speaking</div>
              <button className="mic-button" onClick={startListening}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1C11.2044 1 10.4413 1.31607 9.87868 1.87868C9.31607 2.44129 9 3.20435 9 4V12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12V4C15 3.20435 14.6839 2.44129 14.1213 1.87868C13.5587 1.31607 12.7956 1 12 1Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 10V12C19 13.8565 18.2625 15.637 16.9497 16.9497C15.637 18.2625 13.8565 19 12 19C10.1435 19 8.36301 18.2625 7.05025 16.9497C5.7375 15.637 5 13.8565 5 12V10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 19V23" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 23H16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={`messages-container ${isAiSpeaking || isListening ? 'faded' : ''}`}>
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
              >
                <div className="message-content">{message.text}</div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <button className="mic-button" onClick={startListening}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1C11.2044 1 10.4413 1.31607 9.87868 1.87868C9.31607 2.44129 9 3.20435 9 4V12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12V4C15 3.20435 14.6839 2.44129 14.1213 1.87868C13.5587 1.31607 12.7956 1 12 1Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 10V12C19 13.8565 18.2625 15.637 16.9497 16.9497C15.637 18.2625 13.8565 19 12 19C10.1435 19 8.36301 18.2625 7.05025 16.9497C5.7375 15.637 5 13.8565 5 12V10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19V23" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 23H16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Chat;